import { useMemo, useState } from 'react'
import { AxiosError } from 'axios'
import {
  COMPLIANCE_STUDIOS,
  MACHINERY_DIRECTIVE_STUDIO_ID,
} from '@artem-lex/common-server/complianceStudio'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { CanonicalStandardPickerList } from '@/shared/components/standards/CanonicalStandardPickerList'
import { MachineryStandardTypePicker } from '@/shared/components/standards/MachineryStandardTypePicker'
import type { StandardPickerItem } from '@/shared/hooks/api/standards'
import {
  useListUserStandards,
  useCreateUserStandard,
  useCreateUserStandardFromStandard,
  isManualMachineryStandardType,
  type CreateUserStandardPayload,
} from '@/shared/hooks/api/userStandards'
import { cn } from '@/shared/lib/utils'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

type AddMode = 'fromLibrary' | 'createNew'

type AddStandardModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onAdded?: (userStandardId: string) => void
}

const emptyForm: CreateUserStandardPayload = {
  reference: '',
  title: '',
  inEffect: true,
  complianceStudioIds: [],
}

export function AddStandardModal({
  open,
  onOpenChange,
  onAdded,
}: AddStandardModalProps) {
  const { t } = useTranslation('standardsLibrary')
  const [mode, setMode] = useState<AddMode>('fromLibrary')
  const [search, setSearch] = useState('')
  const [selectedSourceStandardId, setSelectedSourceStandardId] = useState<
    string | null
  >(null)
  const [modalError, setModalError] = useState<string | null>(null)
  const [form, setForm] = useState<CreateUserStandardPayload>(emptyForm)

  const { data: userStandards = [] } = useListUserStandards({ enabled: open })
  const complianceStudios = COMPLIANCE_STUDIOS
  const createFromLibraryMutation = useCreateUserStandardFromStandard()
  const createNewMutation = useCreateUserStandard()

  const excludeReferences = useMemo(
    () => new Set(userStandards.map((userStandard) => userStandard.reference)),
    [userStandards]
  )

  const hasMachineryDirectiveStudio = form.complianceStudioIds.includes(
    MACHINERY_DIRECTIVE_STUDIO_ID
  )
  const showStandardTypePicker = hasMachineryDirectiveStudio

  const handleAddFromLibrary = () => {
    if (!selectedSourceStandardId) {
      return
    }
    setModalError(null)
    createFromLibraryMutation.mutate(
      { sourceStandardId: selectedSourceStandardId },
      {
        onSuccess: (data) => {
          onAdded?.(data.id)
          toast.success(t('addFromLibrary.success'))
          onOpenChange(false)
        },
        onError: (err) => {
          const message =
            err instanceof AxiosError && err.response?.status === 409
              ? t('addFromLibrary.conflict')
              : t('addFromLibrary.addFailed')
          setModalError(message)
        },
      }
    )
  }

  const handleCreateNew = () => {
    setModalError(null)
    const reference = form.reference.trim()
    const title = form.title.trim()
    if (!reference || !title) {
      setModalError(t('createNew.validationRequired'))
      return
    }
    if (form.complianceStudioIds.length === 0) {
      setModalError(t('createNew.validationComplianceStudio'))
      return
    }
    const payload: Parameters<typeof createNewMutation.mutate>[0] = {
      reference,
      title,
      inEffect: form.inEffect,
      complianceStudioIds: form.complianceStudioIds,
      withdrawalOjDate: form.withdrawalOjDate?.trim() || null,
    }
    if (hasMachineryDirectiveStudio && isManualMachineryStandardType(form.type)) {
      payload.type = form.type
    }
    createNewMutation.mutate(payload, {
      onSuccess: (data) => {
        onAdded?.(data.id)
        toast.success(t('createNew.success'))
        onOpenChange(false)
      },
      onError: (err) => {
        const message =
          err instanceof AxiosError && err.response?.status === 409
            ? t('createNew.conflict')
            : t('createNew.addFailed')
        setModalError(message)
      },
    })
  }

  const handleClose = () => {
    setSearch('')
    setSelectedSourceStandardId(null)
    setModalError(null)
    setForm(emptyForm)
    setMode('fromLibrary')
    onOpenChange(false)
  }

  const handleSelectPickerItem = (item: StandardPickerItem) => {
    setSelectedSourceStandardId(item.id)
  }

  const toggleComplianceStudio = (complianceStudioId: string) => {
    setForm((prev) => {
      const isSelected = prev.complianceStudioIds.includes(complianceStudioId)
      const nextComplianceStudioIds = isSelected
        ? prev.complianceStudioIds.filter((id) => id !== complianceStudioId)
        : [...prev.complianceStudioIds, complianceStudioId]
      const stillHasMachineryDirective = nextComplianceStudioIds.includes(
        MACHINERY_DIRECTIVE_STUDIO_ID
      )
      return {
        ...prev,
        complianceStudioIds: nextComplianceStudioIds,
        type: stillHasMachineryDirective ? prev.type : undefined,
      }
    })
  }

  const isPending =
    createFromLibraryMutation.isPending || createNewMutation.isPending

  const addStandardDisabled =
    mode === 'fromLibrary' ? !selectedSourceStandardId || isPending : isPending

  const handleAddStandard =
    mode === 'fromLibrary' ? handleAddFromLibrary : handleCreateNew

  const error = modalError

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className='flex max-h-[90vh] max-w-2xl flex-col'>
        <DialogHeader>
          <DialogTitle>{t('addStandardModal.title')}</DialogTitle>
        </DialogHeader>

        <div className='min-h-0 flex-1 space-y-4 overflow-y-auto'>
          <div className='flex gap-2 border-b pb-2'>
            <button
              type='button'
              onClick={() => {
                setMode('fromLibrary')
                setSelectedSourceStandardId(null)
              }}
              className={cn(
                'rounded px-3 py-1.5 text-sm font-medium',
                mode === 'fromLibrary'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              )}
            >
              {t('addStandardModal.fromLibrary')}
            </button>
            <button
              type='button'
              onClick={() => setMode('createNew')}
              className={cn(
                'rounded px-3 py-1.5 text-sm font-medium',
                mode === 'createNew'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              )}
            >
              {t('addStandardModal.createNew')}
            </button>
          </div>

          {error && (
            <Alert variant='destructive'>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {mode === 'fromLibrary' && (
            <>
              <p className='text-sm text-muted-foreground'>
                {t('addFromLibrary.description')}
              </p>
              <div className='space-y-2'>
                <Label htmlFor='add-standard-search'>
                  {t('addFromLibrary.searchLabel')}
                </Label>
                <Input
                  id='add-standard-search'
                  type='text'
                  placeholder={t('addFromLibrary.searchPlaceholder')}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className='w-full'
                />
              </div>
              <div className='max-h-[50vh] overflow-y-auto rounded-md border p-2'>
                <CanonicalStandardPickerList
                  enabled={open}
                  search={search}
                  excludeReferences={excludeReferences}
                  selectedId={selectedSourceStandardId}
                  onSelect={handleSelectPickerItem}
                  listVariant='card'
                  disabled={isPending}
                />
              </div>
            </>
          )}

          {mode === 'createNew' && (
            <>
              <div className='max-w-xl space-y-4'>
                <p className='text-sm text-muted-foreground'>
                  {t('createNew.description')}
                </p>
                <div className='space-y-2'>
                  <Label htmlFor='create-reference'>
                    {t('createNew.reference')} *
                  </Label>
                  <Input
                    id='create-reference'
                    type='text'
                    value={form.reference}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        reference: event.target.value,
                      }))
                    }
                    placeholder={t('createNew.referencePlaceholder')}
                    className='w-full'
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='create-title'>{t('createNew.title')} *</Label>
                  <Input
                    id='create-title'
                    type='text'
                    value={form.title}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        title: event.target.value,
                      }))
                    }
                    placeholder={t('createNew.titlePlaceholder')}
                    className='w-full'
                  />
                </div>
                <div>
                  <span className='mb-2 block text-sm font-medium'>
                    {t('createNew.complianceStudios')} *
                  </span>
                  <div className='space-y-2'>
                    {complianceStudios.map((complianceStudio) => (
                      <div
                        key={complianceStudio.id}
                        className='flex items-center gap-2'
                      >
                        <Checkbox
                          id={`compliance-studio-${complianceStudio.id}`}
                          checked={form.complianceStudioIds.includes(
                            complianceStudio.id
                          )}
                          onCheckedChange={() =>
                            toggleComplianceStudio(complianceStudio.id)
                          }
                        />
                        <Label
                          htmlFor={`compliance-studio-${complianceStudio.id}`}
                          className='cursor-pointer text-sm font-normal'
                        >
                          {complianceStudio.title}
                        </Label>
                      </div>
                    ))}
                    {complianceStudios.length === 0 && (
                      <p className='text-sm text-muted-foreground'>
                        {t('createNew.noComplianceStudios')}
                      </p>
                    )}
                  </div>
                </div>
                {showStandardTypePicker && (
                  <MachineryStandardTypePicker
                    value={form.type}
                    onChange={(type) => setForm((prev) => ({ ...prev, type }))}
                    disabled={isPending}
                    name='create-new-standard-type'
                  />
                )}
                <div>
                  <span className='mb-2 block text-sm font-medium'>
                    {t('createNew.status')}
                  </span>
                  <div className='flex flex-col gap-2'>
                    <label className='flex items-center gap-2'>
                      <input
                        type='radio'
                        name='create-new-status'
                        checked={form.inEffect}
                        onChange={() =>
                          setForm((prev) => ({
                            ...prev,
                            inEffect: true,
                            withdrawalOjDate: undefined,
                          }))
                        }
                        className='h-4 w-4'
                      />
                      <span className='text-sm'>
                        {t('createNew.statusCurrent')}
                      </span>
                    </label>
                    <label className='flex items-center gap-2'>
                      <input
                        type='radio'
                        name='create-new-status'
                        checked={!form.inEffect}
                        onChange={() =>
                          setForm((prev) => ({ ...prev, inEffect: false }))
                        }
                        className='h-4 w-4'
                      />
                      <span className='text-sm'>
                        {t('createNew.statusWithdrawn')}
                      </span>
                    </label>
                    {!form.inEffect && (
                      <div className='ms-6 mt-2 space-y-2'>
                        <Label htmlFor='create-withdrawal-date'>
                          {t('createNew.withdrawalOjDate')}
                        </Label>
                        <Input
                          id='create-withdrawal-date'
                          type='date'
                          value={
                            form.withdrawalOjDate
                              ? form.withdrawalOjDate.slice(0, 10)
                              : ''
                          }
                          onChange={(event) =>
                            setForm((prev) => ({
                              ...prev,
                              withdrawalOjDate: event.target.value
                                ? event.target.value
                                : null,
                            }))
                          }
                          className='w-full'
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={handleClose} disabled={isPending}>
            {t('common:actions.close')}
          </Button>
          <Button onClick={handleAddStandard} disabled={addStandardDisabled}>
            {t('common:actions.add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
